import * as React from 'react';
import { Button, Icon, Modal, Select } from '@teamsnap/teamsnap-ui';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import api from 'core/api';
import { FormResult } from './types';

type Props = {
  selectedRecords: any[];
  selectedFormResult: FormResult;
  show: boolean;
  onClose: () => any;
  onSubmit: () => any;
  selectedRole: string;
  setSelectedRole: (role: string) => any;
  orgId: string;
  formId: string;
};

const feedbackStyles: React.CSSProperties = {
  minWidth: '0',
  width: '350px',
  textAlign: 'center',
};
const formStyles: React.CSSProperties = {
  minWidth: '0',
  width: '451px',
  textAlign: 'start',
};

const FeedbackMessage = ({ onSubmit }) => (
  <div className="u-flex u-flexCol">
    <div className="u-flex u-flexAlignSelfCenter u-flexAlignItemsCenter">
      <Icon name="task-alt" mods="u-colorPositive u-spaceRightSm" />
      <p className="u-textBold">Your roster profiles are now being created!</p>
    </div>

    <p className="u-spaceTopMd u-textCenter">
      Depending on the number of roster profiles this may take a few moments to
      complete. You can add filters or search your data to view the updated
      roster status for your selected participant(s).
    </p>

    <Button
      mods="u-flexAlignSelfEnd u-spaceTopMd"
      color="primary"
      onClick={onSubmit}
    >
      OK
    </Button>
  </div>
);

const RosteringModal = ({
  selectedRecords,
  orgId,
  formId,
  selectedFormResult,
  show,
  onClose,
  onSubmit,
  selectedRole,
  setSelectedRole,
}: Props) => {
  const [mode, setMode] = React.useState<'form' | 'feedback'>('form');

  const selectedRecordsLength =
    selectedRecords?.filter((p) => p.rosterStatus === 'No Roster Profile')
      ?.length || 0;

  const save = async () => {
    try {
      const data = {
        role: selectedRole,
        form_result_ids: selectedFormResult
          ? [selectedFormResult.formResultId]
          : selectedRecords
              ?.filter((p) => p.rosterStatus === 'No Roster Profile')
              ?.map((r) => r.formResultId),
      };
      await api.post(
        `/organizations/${orgId}/roster_participants/${formId}`,
        data
      );
      setMode('feedback');
    } catch (e) {
      reactiveFeedbackMessage({
        message:
          'Oops! Failed to roster participant(s). Please try again or contact TeamSnap support for help.',
        isError: true,
        autoclose: true,
      });
      onSubmit();
    }
  };

  return (
    <Modal
      show={show}
      allowOverlayClose={mode === 'form'}
      style={mode === 'form' ? formStyles : feedbackStyles}
      closeFn={onClose}
      heading="Ready to Roster"
      showClose={mode === 'form'}
    >
      {mode === 'feedback' && (
        <FeedbackMessage
          onSubmit={() => {
            setMode('form');
            onSubmit();
          }}
        />
      )}

      {mode === 'form' && (
        <>
          <div className="u-flex u-padBottomLg">
            <div className="u-flex u-padRightLg u-textBold u-fontSizeLg">
              {selectedFormResult ? 'Selected Participant:' : 'Selected Group'}
            </div>
            <div className="u-flex u-fontSizeLg">
              {selectedFormResult
                ? `${selectedFormResult.participantFirstName || ''} ${
                    selectedFormResult.participantLastName || ''
                  }`
                : `${selectedRecordsLength} Completed ${
                    selectedRecordsLength === 1
                      ? 'Registration'
                      : 'Registrations'
                  }`}
            </div>
          </div>
          <div className="u-padBottomLg">
            <div className="u-padBottomSm u-textBold u-fontSizeLg">
              Assign a Role
            </div>
            <Select
              name="roster"
              options={[
                {
                  label: 'Player',
                  value: 'player',
                },
                {
                  label: 'Player & Manager',
                  value: 'player_and_manager',
                },
                {
                  label: 'Non-Player & Manager',
                  value: 'non_player_and_manager',
                },
                {
                  label: 'Non-Player',
                  value: 'non_player',
                },
              ]}
              inputProps={{
                onChange: (e) => setSelectedRole(e.target.value),
                value: `${selectedRole}`,
              }}
            />
          </div>
          <div className="buttons-wrapper">
            <Button mods="u-spaceRightSm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              mods="Button Button--primary"
              onClick={save}
              isDisabled={
                selectedFormResult
                  ? !selectedRole
                  : selectedRecordsLength === 0 || !selectedRole
              }
            >
              Save
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default RosteringModal;
